import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { DrawerService } from '../../../../core/services/drawer.service';
import {
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
  distinctUntilChanged,
  shareReplay,
} from 'rxjs';
import {
  Title,
  UserListModel,
  UserModel,
} from '../../../manage-organization/manage-organization.interface';
import {
  OrganizationDetailModel,
  OrganizationModel,
  userDetailModel,
} from '../../../organizations/organization.interface';
import { Role, RoleService } from '../../../../shared/services/role.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { ManageOrganizationService } from '../../../manage-organization/manage-organization.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonService } from '../../../../shared/services/common.service';
import { CommonModule } from '@angular/common';
import { LoaderService } from '../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { UserDrawerParams } from '../user-manage';
import { OrganizationService } from '../../../organizations/organization.service';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { LookupItem } from '../../../../core/models/lookup.models';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-edit-user',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzSelectModule,
    NzInputModule,
    NzSwitchModule,
    NzTableModule,
    NzButtonModule,
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    NzSpinModule,
    NzIconModule,
    NzToolTipModule,
    AngularSvgIconModule,
    FormsModule,
  ],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss',
})
export class EditUserComponent implements OnInit, OnDestroy {
  private readonly drawerService = inject(DrawerService);
  private readonly manageOrgService = inject(ManageOrganizationService);
  private readonly organizationService = inject(OrganizationService);
  private readonly commonService = inject(CommonService);
  private readonly roleService = inject(RoleService);
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();
  private loaderService = inject(LoaderService);
  private cdr = inject(ChangeDetectorRef);
  private isTitleLoad = false;

  private isOrgChanging = false;
  private isInitialLoad = true;

  userDetails = {
    userData: {} as UserListModel,
    orgData: {} as OrganizationDetailModel,
    roleList: [] as Role[],
    mode: 'CREATE',
    from: 'edit-user',
  } as UserDrawerParams;

  _statusList = [
    {
      id: 1,
      value: 'ACTIVE',
    },
    {
      id: 2,
      value: 'INACTIVE',
    },
    {
      id: 3,
      value: 'PENDING',
    },
  ];

  organizations$!: Observable<LookupItem[]>;
  allowedOrganizations$: Observable<OrganizationModel[]> = of([]);
  searchOrganizationTerm = '';
  titles$!: Observable<Title[]>;
  loading$: Observable<boolean> = of(false);
  mappedRoles: number[] = [];
  userForm!: FormGroup;
  isSubmitting = false;
  DSARoleId = 0;
  listOfSelection = [
    {
      text: 'Select All Rows',
      onSelect: () => {
        this.onAllChecked(true);
      },
    },
  ];

  checked = false;
  indeterminate = false;
  listOfCurrentPageData: readonly Role[] = [];
  setOfCheckedId = new Set<number>();
  userData: userDetailModel | null = null;

  readonly authService = inject(AuthService);

  ngOnInit(): void {
    this.authService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.userData = user;
      });
    this.initForm();
    this.getDrawerParams();

    setTimeout(() => {
      this.userForm
        .get('organizationId')
        ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
        .subscribe(val => {
          const orgId =
            Array.isArray(val) && val.length > 0
              ? val[0]
              : typeof val === 'number'
                ? val
                : Array.isArray(val)
                  ? null
                  : val;

          if (orgId && typeof orgId === 'number') {
            this.isOrgChanging = true;
            this.loadTitlesForOrg(Number(orgId));
            if (!this.isInitialLoad) {
              this.userForm.get('titleId')?.setValue(null);
            }
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getContextOrgId(): number {
    const fromUser = this.userDetails?.userData?.organizationId
      ?.split(',')
      .map(id => Number(id.trim()));
    const fromOrgData = this.userDetails?.orgData?.organizationId;
    const fromSelected = this.userDetails?.selectedOrg;
    const id = fromUser ?? [fromOrgData ?? fromSelected ?? 0];
    return Number(id[0]) || 0;
  }

  initForm(): void {
    this.userForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      organizationId: [[], Validators.required],
      titleId: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      statusId: [this.formMode === 'CREATE' ? 3 : null, Validators.required],
    });
  }

  getDrawerParams(): void {
    this.drawerService
      .getParams<UserDrawerParams>(DRAWER_IDS.MANAGE_EDIT_USER)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(async params => {
        if (params) {
          this.userDetails = params;

          if (this.userDetails.roleList.length === 0) {
            this.roleService
              .getAllRoles()
              .pipe(takeUntil(this.destroy$))
              .subscribe(response => {
                this.userDetails.roleList = response.result;
                if (this.userDetails.roleList.length > 0) {
                  const dsarRole = this.userDetails.roleList.find(
                    role => role.name === 'District Settings Admin'
                  );
                  this.DSARoleId = dsarRole ? dsarRole.id : 0;
                }
              });
          }

          if (this.userDetails.userData?.roles) {
            const userRoles = this.userDetails.userData.roles
              .split(',')
              .map(role => role.trim());

            this.userDetails.roleList.forEach(role => {
              if (userRoles.includes(role.name)) {
                this.setOfCheckedId.add(role.id);
              }
            });
          }

          this.loadTitles();

          if (this.userDetails.mode === 'EDIT' && this.userDetails.userData) {
            let firstName = '';
            let lastName = '';

            if (this.userDetails.userData.name) {
              const nameParts = this.userDetails.userData.name.split(' ');
              firstName = nameParts[0] || '';
              lastName = nameParts.slice(1).join(' ') || '';
            }
            let organizations: number[] = [];
            if (this.userDetails.userData.organizationId) {
              organizations = this.userDetails.userData.organizationId
                .split(',')
                .map(id => Number(id.trim()));
            }
            this.userForm.patchValue({
              firstName: firstName,
              lastName: lastName,
              email: this.userDetails.userData.email || '',
              statusId: this.getStatusId(this.userDetails.userData.status),
              organizationId: organizations.length > 0 ? organizations : [],
            });

            const orgId =
              this.userDetails.from === 'user-search'
                ? this.userDetails.userData.organizationId
                : (this.userDetails.orgData?.organizationId ??
                  this.userDetails.selectedOrg ??
                  null);

            if (orgId) {
              const orgIdArray =
                typeof orgId === 'string'
                  ? orgId.split(',').map(id => Number(id.trim()))
                  : [Number(orgId)];
              this.userForm.get('organizationId')?.setValue(orgIdArray);
            }

            if (this.userDetails.userData.title) {
              this.getTitleId(this.userDetails.userData.title)
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe(titleId => {
                  this.userForm.get('titleId')?.setValue(titleId);
                });
            }
          }

          this.initAllowedOrganizations();
          await this.refreshCheckedStatus();
        }
      });
  }

  getStatusId(statusValue: string): number {
    const status = this.statusList.find(s => s.value === statusValue);
    return status ? status.id : 0;
  }

  getTitleId(titleValue: string): Observable<number> {
    return this.titles$.pipe(
      map(titles => {
        const normalizedSearch = titleValue.trim().toLowerCase();

        const title = titles.find(
          t => t.title.toLowerCase().trim() === normalizedSearch
        );
        return title ? title.id : 0;
      })
    );
  }

  onTitleChange(titleId: number) {
    if (!titleId || !this.titles$) {
      return;
    }

    this.titles$.pipe(take(1), takeUntil(this.destroy$)).subscribe(titles => {
      if (!titles || titles.length === 0) {
        return;
      }

      const selectedTitle = titles.find(t => t.id === titleId);

      const shouldAutoMapRoles =
        selectedTitle?.isBaseLined && this.userDetails.mode === 'CREATE';

      if (shouldAutoMapRoles) {
        if (this.isTitleLoad && !this.isOrgChanging) {
          this.setOfCheckedId.clear();
        }
        this.loadRolesMappedToTitles(titleId);
        this.isOrgChanging = false;
        this.isInitialLoad = false;
      } else {
        this.refreshCheckedStatus();
        this.isOrgChanging = false;
        this.isInitialLoad = false;
      }
      this.isTitleLoad = true;
    });
  }
  updateCheckedSet(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  onItemChecked(id: number, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus();
  }

  onAllChecked(value: boolean): void {
    this.listOfCurrentPageData.forEach(item =>
      this.updateCheckedSet(item.id, value)
    );
    this.refreshCheckedStatus();
  }

  onCurrentPageDataChange($event: readonly Role[]): void {
    this.listOfCurrentPageData = $event;
    this.refreshCheckedStatus();
  }

  refreshCheckedStatus(): void {
    this.checked = this.listOfCurrentPageData.every(item =>
      this.setOfCheckedId.has(item.id)
    );
    this.indeterminate =
      this.listOfCurrentPageData.some(item =>
        this.setOfCheckedId.has(item.id)
      ) && !this.checked;
  }

  onCancel(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_EDIT_USER);
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      return;
    }

    if (this.setOfCheckedId.size === 0) {
      this.commonService.notify('error', 'Please select at least one role.');
      return;
    }

    this.isSubmitting = true;
    const formData = this.userForm.value;

    const selectedOrgIdRaw = formData.organizationId;
    const selectedOrgId = Array.isArray(selectedOrgIdRaw)
      ? selectedOrgIdRaw[0]
      : selectedOrgIdRaw;
    const resolvedOrgId = Number(selectedOrgId || this.getContextOrgId() || 0);

    if (!resolvedOrgId || resolvedOrgId <= 0) {
      this.isSubmitting = false;
      this.commonService.notify('error', "Please select user's School.");
      return;
    }
    let parentOrgId: number | null = null;
    const roleIds = Array.from(this.setOfCheckedId);
    if (roleIds.includes(this.DSARoleId) && roleIds.length > 1) {
      this.allowedOrganizations$
        .pipe(takeUntil(this.destroy$))
        .subscribe(orgs => {
          const selectedOrg = orgs.find(o => o.id === selectedOrgIdRaw[0]);
          parentOrgId = selectedOrg ? selectedOrg.parentOrganizationId : null;
        });
    }
    let orgIds: number[] = [];
    if (this.userDetails.userData && this.userDetails.userData.organizationId) {
      orgIds = this.userDetails.userData.organizationId
        .split(',')
        .map(id => Number(id.trim()));
    }
    const payload: UserModel = {
      id: this.userDetails.userData?.id,
      organizationId: resolvedOrgId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      titleId: formData.titleId,
      statusId: formData.statusId,
      roleIds: Array.from(this.setOfCheckedId),
      isAllUsers: this.userDetails.from === 'user-search' ? true : false,
      isParentOrg:
        this.userDetails.from === 'user-search'
          ? orgIds && orgIds.length > 1
            ? orgIds[0]
            : 0
          : (this.userDetails.orgData?.organizationId ??
            this.userDetails.selectedOrg ??
            0),
    };

    payload.organizationIds = Array.isArray(formData.organizationId)
      ? formData.organizationId
      : [formData.organizationId].filter(
          (v: number | null | undefined) => typeof v === 'number' && v > 0
        );
    if (parentOrgId && typeof parentOrgId === 'number' && parentOrgId > 0) {
      const orgIdsArray = Array.isArray(payload.organizationIds)
        ? payload.organizationIds
        : typeof payload.organizationIds === 'number'
          ? [payload.organizationIds]
          : [];
      payload.organizationIds = [...orgIdsArray, parentOrgId];
    }

    if (this.userDetails.mode === 'EDIT' && this.userDetails.userData?.id) {
      this.manageOrgService
        .editOrgUser(payload, this.userDetails.pageIndex)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isSubmitting = false;
            this.commonService.notify('success', 'User updated successfully.');
            this.drawerService.closeAllDrawers();
          },
          error: error => {
            this.isSubmitting = false;
            this.commonService.notify(
              'error',
              error.message || 'Error updating user'
            );
          },
        });
    } else {
      this.manageOrgService
        .createOrgUser(payload, this.userDetails.pageIndex)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isSubmitting = false;
            this.commonService.notify('success', 'User created successfully.');
            this.drawerService.closeAllDrawers();
          },
          error: error => {
            this.isSubmitting = false;
            this.commonService.notify(
              'error',
              error.message || 'Error creating user'
            );
          },
        });
    }
  }

  loadTitles(): void {
    const orgId = this.getContextOrgId();
    this.loadTitlesForOrg(orgId);
  }

  private loadTitlesForOrg(orgId: number): void {
    this.titles$ = this.manageOrgService
      .getTitles({ organizationId: orgId })
      .pipe(
        map(response => response.result),
        shareReplay(1),
        takeUntil(this.destroy$)
      );
  }
  loadRolesMappedToTitles(titleId: number): void {
    this.manageOrgService
      .getMappedRolesToTitle(titleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          if (res.result) {
            this.mappedRoles = res.result;
            if (this.userDetails.mode === 'CREATE') {
              this.userDetails.roleList.forEach(role => {
                if (this.mappedRoles.includes(role.id)) {
                  this.setOfCheckedId.add(role.id);
                }
              });
              this.setOfCheckedId = new Set(this.setOfCheckedId);
              this.cdr.detectChanges();
            }
          }
          this.refreshCheckedStatus();
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  initAllowedOrganizations(): void {
    setTimeout(() => {
      this.loadAllowedOrganizations();
    });
  }

  loadAllowedOrganizations(): void {
    if (this.userData?.isSuperAdmin) {
      this.allowedOrganizations$ = this.organizationService
        .getOrganizationsByUserId(this.userDetails.userData?.id)
        .pipe(
          map(response => {
            const organizations = response?.result || [];
            return Array.isArray(organizations) ? organizations : [];
          }),
          takeUntil(this.destroy$)
        );
    } else {
      this.allowedOrganizations$ = this.organizationService
        .getOrganizationsToSwitch()
        .pipe(
          map(response => {
            const organizations = response?.result || [];
            return Array.isArray(organizations) ? organizations : [];
          }),
          takeUntil(this.destroy$)
        );
    }
  }

  onOrganizationSearch(searchValue: string): void {
    this.searchOrganizationTerm = searchValue;

    if (!searchValue.trim()) {
      this.loadAllowedOrganizations();
      return;
    }

    this.allowedOrganizations$ = this.organizationService
      .getOrganizationsToSwitch()
      .pipe(
        map(response => {
          const organizations = response?.result || [];
          const validOrganizations = Array.isArray(organizations)
            ? organizations
            : [];

          return validOrganizations.filter(org =>
            org.organizationName
              ?.toLowerCase()
              .includes(searchValue.toLowerCase())
          );
        }),
        takeUntil(this.destroy$)
      );
  }

  get formMode(): string {
    return this.userDetails.mode;
  }

  get statusList() {
    if (this.formMode === 'CREATE') {
      return this._statusList.filter(status => status.value !== 'ACTIVE');
    }
    return this._statusList;
  }

  get isActive(): boolean {
    return this.userForm.get('statusId')?.value === 1;
  }

  setActiveStatus(active: boolean): void {
    this.userForm.get('statusId')?.setValue(active ? 1 : 2);
  }
}
